/**
 * React Query hooks for loan rules: patron category defaults and their exceptions.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post } from "../client";
import type {
  LoanPolicy,
  LoanPolicyInput,
  PatronCategory,
  PatronCategoryIdentityInput,
  PatronCategoryInput,
  PatronCategoryRulesInput,
  RecordType,
  ResolvedLoanRule,
} from "../types";

const LOAN_POLICIES_KEY = "loan-policies";
const RESOLVED_RULE_KEY = "resolved-loan-rule";
// Shared with usePatronCategories so the category list refreshes after an edit
const CATEGORIES_KEY = "patron-categories";

/**
 * Loan rule exceptions of a patron category.
 */
export function useLoanPolicies(patronCategoryId: string | undefined) {
  return useQuery({
    queryKey: [LOAN_POLICIES_KEY, patronCategoryId],
    queryFn: () =>
      get<LoanPolicy[]>(
        `/circulation/loan-policies?patronCategoryId=${encodeURIComponent(patronCategoryId ?? "")}`
      ),
    enabled: !!patronCategoryId,
  });
}

function useInvalidateLoanRules() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: [LOAN_POLICIES_KEY] });
    void queryClient.invalidateQueries({ queryKey: [RESOLVED_RULE_KEY] });
  };
}

export function useCreateLoanPolicy() {
  const invalidate = useInvalidateLoanRules();
  return useMutation({
    mutationFn: (input: LoanPolicyInput) => post<LoanPolicy>("/circulation/loan-policies", input),
    onSuccess: invalidate,
  });
}

export function useUpdateLoanPolicy() {
  const invalidate = useInvalidateLoanRules();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<LoanPolicyInput> }) =>
      patch<LoanPolicy>(`/circulation/loan-policies/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteLoanPolicy() {
  const invalidate = useInvalidateLoanRules();
  return useMutation({
    mutationFn: (id: string) => del(`/circulation/loan-policies/${id}`),
    onSuccess: invalidate,
  });
}

/**
 * Update the default loan rules of a patron category.
 */
export function useUpdateCategoryRules() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateLoanRules();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PatronCategoryRulesInput }) =>
      patch<PatronCategory>(`/circulation/patron-categories/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CATEGORIES_KEY] });
      invalidate();
    },
  });
}

/**
 * Create a patron category with its default loan rules.
 */
export function useCreatePatronCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PatronCategoryInput) =>
      post<PatronCategory>("/circulation/patron-categories", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CATEGORIES_KEY] });
    },
  });
}

/**
 * Rename, recode, retype or (de)activate a patron category.
 */
export function useUpdatePatronCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PatronCategoryIdentityInput }) =>
      patch<PatronCategory>(`/circulation/patron-categories/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CATEGORIES_KEY] });
    },
  });
}

/**
 * Delete a patron category; the server refuses while patrons belong to it.
 */
export function useDeletePatronCategory() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateLoanRules();
  return useMutation({
    mutationFn: (id: string) => del(`/circulation/patron-categories/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CATEGORIES_KEY] });
      // Its exceptions are deleted with it
      invalidate();
    },
  });
}

/**
 * The rule the server applies to a category borrowing an item type at a location.
 */
export function useResolvedLoanRule(
  patronCategoryId: string | undefined,
  itemType: RecordType | undefined,
  locationId: string | undefined
) {
  return useQuery({
    queryKey: [RESOLVED_RULE_KEY, patronCategoryId, itemType, locationId],
    queryFn: () => {
      const params = new URLSearchParams({
        patronCategoryId: patronCategoryId ?? "",
        itemType: itemType ?? "",
        locationId: locationId ?? "",
      });
      return get<ResolvedLoanRule>(`/circulation/loan-rules/resolve?${params.toString()}`);
    },
    enabled: !!patronCategoryId && !!itemType && !!locationId,
  });
}
